import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  labelledBy?: string;
  describedBy?: string;
}

const focusable = 'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function Modal({ open, onClose, children, ariaLabel, labelledBy, describedBy }: ModalProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // lock background scroll while modal is open
    let prevOverflow: string | null = null;
    try {
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } catch {}
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const nodes = ref.current?.querySelectorAll<HTMLElement>(focusable);
      if (!nodes || nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey);
    const first = ref.current?.querySelector<HTMLElement>(focusable);
    first?.focus();
    return () => {
      document.removeEventListener("keydown", handleKey);
      try {
        if (prevOverflow !== null) document.body.style.overflow = prevOverflow;
      } catch {}
    };
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg/50"
      onMouseDown={onClose}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        className="max-h-[90vh] max-w-lg overflow-auto rounded-md bg-surface p-6 shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
